"use client";

import React, { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "../../components/Spinner";
import { useTranslation } from "react-i18next";
import SectionTabs from "../../components/ui/SectionTabs";
import ActivityBoard from "../../components/Workflows/ActivityBoard";
import { LEGACY_VIEWS, legacyViewRedirect } from "../../lib/navigation";
import MetricsService, {
  DEFAULT_PERIOD,
  PERIOD_DAYS,
  periodToRange,
} from "../../services/metrics.service";
import AssistantService from "../../services/assistant.service";
import { useOrganization } from "../../contexts/OrganizationContext";
import { useProjects } from "../../hooks/useOrganizations";

import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { PageHeader } from "../../components/dashboard/PageHeader";
import { DashboardTabs } from "../../components/dashboard/DashboardTabs";
import { AgentScoreboard } from "../../components/dashboard/AgentScoreboard";
import { ScopeBar, ALL_PROJECTS } from "../../components/dashboard/ScopeBar";
import { SeriesChart } from "../../components/dashboard/SeriesChart";
import {
  CoinsByResourceCard,
  TopSpendersCard,
} from "../../components/dashboard/CoinsCards";
import {
  RecentFailuresCard,
  TopWorkflowsCard,
} from "../../components/dashboard/WorkflowCards";
import {
  CoverageNotice,
  ModelUsageCard,
} from "../../components/dashboard/ModelUsageCard";
import {
  formatChange,
  formatCoins,
  formatCompact,
  formatDuration,
  formatUsd,
  toNumber,
} from "../../components/dashboard/format";
import {
  Activity,
  AlertTriangle,
  ArrowDownLeft,
  ArrowUpRight,
  Bot,
  CheckCircle,
  Play,
  Headset,
  IdCard,
  Plug,
  Clock,
  Coins,
  Gauge,
  LayoutDashboard,
  Sparkles,
  Workflow as WorkflowIcon,
  Zap,
} from "lucide-react";
import { AnimatedValue } from "../../components/dashboard/AnimatedValue";
import { Sparkline } from "../../components/dashboard/Sparkline";
import { GaugeRing } from "../../components/dashboard/GaugeRing";
import { AnalyticsHero } from "../../components/dashboard/AnalyticsHero";
import { cn } from "../../lib/utils";
import AppBackdrop from "../../components/ui/AppBackdrop";

const RecentActivities = React.lazy(() =>
  import("../../components/recent-activities/recent-activities").then((m) => ({
    default: m.RecentActivities,
  }))
);
const WorkflowsView = React.lazy(() =>
  import("../../components/Workflows/GlobalWorkflowsView")
);
const AssistantsView = React.lazy(() =>
  import("../../components/AssistantsView/AssistantsView")
);
const AvatarsView = React.lazy(() =>
  import("../../components/AvatarsView/AvatarsView")
);
const ProjectsView = React.lazy(() =>
  import("../../components/ProjectsView/ProjectsView")
);
const IntegrationsView = React.lazy(() => import("../Integrations/IntegrationsView"));
const DatabaseView = React.lazy(() => import("../Database/DatabaseView"));
const KnowledgeBasesView = React.lazy(() => import("../KnowledgeBases/KnowledgeBasesView"));
const ShopView = React.lazy(() => import("../Shop"));

const Loader = () => (
  <div className="flex w-full flex-col items-center justify-center space-y-4 py-12">
    <Spinner size="lg" color="purple" />
    <p className="text-sm text-gray-400">Loading dashboard...</p>
  </div>
);

export default function AIDashboard() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const viewParam = searchParams.get("view");

  const tabParam = searchParams.get("tab");

  const mapViewParam = (param) => {
    switch (param) {
      case "Integrations":
        return "Connect";
      // "Agents" es el término oficial del front (la URL es ?view=Agents); la vista
      // interna sigue llamándose "Assistants" (como el backend assistant_service).
      // Sin esto, ?view=Agents no matchea ningún case y se rompe en recarga fresca.
      case "Agents":
        return "Assistants";
      // Avatars y Credentials dejaron de ser secciones propias: ahora son una
      // pestaña dentro de Agents y de Connections. El destino sigue existiendo,
      // solo cambió de sitio.
      case "Avatars":
        return "Assistants";
      case "Credentials":
        return "Connect";
      default:
        return param;
    }
  };

  const [view, setView] = useState(mapViewParam(viewParam) || "Dashboard");

  // Las pestañas viven en la URL para que recargar no vuelva a la primera y para
  // que se pueda enlazar una concreta.
  const activeTab = tabParam || (viewParam && LEGACY_VIEWS[viewParam]?.tab) || null;

  // Los enlaces viejos siguen funcionando: se reescriben a la pestaña nueva,
  // con el resto de la query intacta. Armar la URL desde cero borraba el
  // `templateId` con el que se abre un avatar, y el detalle no llegaba a
  // montarse nunca.
  useEffect(() => {
    const target = legacyViewRedirect(location.search);
    if (target) navigate(target, { replace: true });
  }, [location.search, navigate]);

  const goToTab = useCallback(
    (urlView, tab) => navigate(`/dashboard?view=${urlView}&tab=${tab}`, { replace: true }),
    [navigate]
  );

  // Keep the view in sync with the URL. When ?view= is absent — e.g. clicking
  // "Dashboard" in the rail while already on a sub-view like /dashboard?view=Projects —
  // fall back to the Dashboard home instead of leaving the old sub-view mounted.
  useEffect(() => {
    setView(viewParam ? mapViewParam(viewParam) : "Dashboard");
  }, [viewParam]);

  const navigateToView = useCallback(
    (label) => {
      const viewMapping = {
        Dashboard: "Dashboard",
        Projects: "Projects",
        Assistants: "Agents",
        Workflows: "Workflows",
        Database: "Database",
        Connect: "Integrations",
        Shop: "Shop",
      };

      setView(label);
      const urlView = viewMapping[label] || label;
      navigate(`/dashboard?view=${urlView}`, { replace: true });
    },
    [navigate]
  );

  const handleCreateWorkflow = useCallback(() => {
    navigateToView("Projects");
  }, [navigateToView]);

  const isDashboardView = view === "Dashboard";

  const renderMainContent = () => {
    // El aire de la derecha es de escritorio: en móvil el rail no está y los
    // 16px de `p-4` son todo el margen que caben sin comerse la tarjeta.
    const contentShellClass =
      "rail-mar mr-0 mt-1 min-h-[calc(100vh-8.5rem)] md:mr-4";

    switch (view) {
      case "Dashboard":
        // El board de actividad ya existia, pero vivia dentro de Workflows: una
        // vista sobre todo lo que pasa, enterrada bajo un tipo de objeto. Aqui
        // es donde le toca. Usage se le cuelga como una pestana mas para que
        // quede una sola barra y no una barra de seccion con otra dentro.
        return (
          <div className={`${contentShellClass} p-4 md:p-8`}>
            <ActivityPanel onCreateWorkflow={handleCreateWorkflow} />
          </div>
        );
      // El workspace de un proyecto —sus agentes, sus workflows, su galería y
      // el chat de un agente— vive aquí, en la app, y no en configuración.
      // Configuración lista y administra proyectos; browsear lo que hay dentro
      // es otra cosa, y meterla ahí dejaba el chat de un agente dentro de una
      // pantalla de ajustes.
      // `mr-0` en móvil por lo mismo que `contentShellClass`: sin rail no hay a
      // qué dejarle aire, y 16px a la derecha contra 0 a la izquierda se ve
      // torcido.
      case "Projects":
        return (
          <div className="rail-mar mr-0 mt-1 h-full p-4 md:mr-4 md:p-8">
            <Suspense fallback={<Loader />}>
              <ProjectsView />
            </Suspense>
          </div>
        );
      case "Workflows":
        return (
          <div className="rail-mar h-full">
            <Suspense fallback={<Loader />}>
              <WorkflowsView />
            </Suspense>
          </div>
        );
      case "Assistants": {
        // Un avatar es la plantilla de la que sale un agente: son el mismo
        // trabajo en dos momentos, así que viven en la misma sección.
        const tab = activeTab === "avatars" ? "avatars" : "agents";
        // Las pestañas las pinta la sección y no la vista hija: si vivieran
        // dentro, desaparecerían en cada estado de carga, error o detalle — y
        // quedarse sin navegación a mitad de una carga es lo peor que puede
        // pasar aquí. Las hijas solo ocultan su título para no repetirlo.
        return (
          <div className={`${contentShellClass} p-4 md:p-8`}>
            {/* La sección no se nombraba a sí misma: las pestañas decían
                "Agents / Avatars" y encima no había nada. En escritorio se
                perdona —el rail lleva el destino resaltado—; en móvil no hay
                rail, así que sin esto la pantalla no dice dónde estás. Y es
                también el sitio de la hamburguesa: `PageHeader` la pinta. */}
            <PageHeader
              className="mb-4"
              title={t("sidebar.agents", "Agents")}
              subtitle="Create, manage and deploy AI agents that work for your business."
            />
            <SectionTabs
              className="mb-4"
              ariaLabel="Agents"
              active={tab}
              onChange={(next) => goToTab("Agents", next)}
              tabs={[
                { key: "agents", label: t("sidebar.agents", "Agents"), Icon: Headset },
                { key: "avatars", label: t("sidebar.avatars", "Avatars"), Icon: Bot },
              ]}
            />
            <Suspense fallback={<Loader />}>
              {tab === "avatars" ? (
                <AvatarsView hideTitle />
              ) : (
                <AssistantsView hideTitle onCreateNew={() => navigateToView("Projects")} />
              )}
            </Suspense>
          </div>
        );
      }
      // Sin pestañas: las API keys son de la cuenta —identifican tu cuenta ante
      // la API— y ya viven en su configuración. Aquí quedan las conexiones a
      // sistemas externos, que es lo que esta sección siempre dijo ser.
      case "Connect": {
        return (
          <div className={`${contentShellClass} p-4 md:p-8`}>
            <Suspense fallback={<Loader />}>
              {/* Sin `hideTitle`: el título lo daban las pestañas que estaban
                  encima, y al quitarlas la sección se quedaba sin encabezado. */}
              <IntegrationsView />
            </Suspense>
          </div>
        );
      }
      case "Database":
        return (
          <div className={`${contentShellClass} p-4 md:p-8`}>
            <Suspense fallback={<Loader />}>
              <DatabaseView />
            </Suspense>
          </div>
        );
      case "Knowledge":
        return (
          <div className={`${contentShellClass} p-4 md:p-8`}>
            <Suspense fallback={<Loader />}>
              <KnowledgeBasesView />
            </Suspense>
          </div>
        );
      case "Shop":
        return (
          <div className={`${contentShellClass} p-4 md:p-8`}>
            <Suspense fallback={<Loader />}>
              <ShopView />
            </Suspense>
          </div>
        );
      default:
        return (
          <div className={`${contentShellClass} p-4 md:p-8`}>
            <HomePanel onCreateWorkflow={handleCreateWorkflow} />
          </div>
        );
    }
  };

  return (
    <div
      className={`relative ${view === "Projects" || view === "Workflows" ? "h-screen" : "min-h-screen"} overflow-hidden text-white ${
        isDashboardView ? "bg-black" : "bg-gray-950"
      }`}
    >
      {/* El halo estaba escrito a mano aquí, así que las pantallas añadidas
          después salían en negro plano y se notaba el corte al cambiar de una a
          otra. Ahora sale de un componente que también usan Support y tarifas. */}
      <AppBackdrop withBase={!isDashboardView} />

      <main className={`relative z-10 ${view === "Projects" || view === "Workflows" ? "h-full" : "pt-12"}`}>{renderMainContent()}</main>
    </div>
  );
}

/**
 * The Analytics home panel.
 *
 * Scope (which project) and period (how far back) live in the URL, so a filtered
 * dashboard is a link you can send to someone. Everything below is read from the
 * backend's DashboardMetrics payload — three families: coins spent, workflows run,
 * agents called.
 */
/**
 * La seccion Activity: el board de actividad que ya existia, mas el consumo
 * como una pestana suya.
 *
 * El board necesita un proyecto — sus contadores y su vista por agente salen de
 * /executions/, que exige project_id en el backend (Query(...) en el router del
 * runner). Solo el heatmap acepta cuenta entera. Por eso al entrar siempre hay
 * un proyecto elegido: mostrar "todos" y pintar cero seria mentir sobre que no
 * paso nada. Usage si funciona con todos, porque tiene su propio endpoint.
 */
function ActivityPanel({ onCreateWorkflow }) {
  const { projects, loading: projectsLoading } = useProjects();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get("project") || ALL_PROJECTS;
  const projectList = useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);

  const setProject = (value) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === ALL_PROJECTS) next.delete("project");
    else next.set("project", value);
    setSearchParams(next, { replace: true });
  };

  // Un `?project=` que no está en la lista actual es de otra organización: al
  // cambiar de contexto el parámetro sobrevive en la URL y el proyecto no.
  // Mandarlo igual pedía /executions/ con un id ajeno y devolvía 403.
  const knownProject =
    projectId !== ALL_PROJECTS && projectList.some((p) => p.id === projectId);

  const activeTab = searchParams.get("tab") || "overview";
  const onBoardTab = activeTab !== "usage";

  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "overview") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  // Las tres pestanas del board necesitan un proyecto concreto. Si se llega con
  // "todos" —viniendo de Usage, que si lo soporta— se elige el primero, y el
  // selector lo muestra: el alcance cambia a la vista, no a espaldas del usuario.
  //
  // La condición se apoya en `projectsLoading`, que significa "todavía no hay
  // respuesta fiable" y no "hay una petición en vuelo": sin esa espera, un
  // enlace directo a un proyecto válido se sobrescribiría con el primero de la
  // lista mientras la lista aún está vacía.
  useEffect(() => {
    if (!onBoardTab || projectsLoading || !projectList.length) return;
    if (projectId !== ALL_PROJECTS && knownProject) return;
    const next = new URLSearchParams(searchParams);
    next.set("project", projectList[0].id);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBoardTab, projectsLoading, projectId, knownProject, projectList.length]);

  // Mientras el proyecto no esté confirmado en la lista no se pasa hacia abajo:
  // si no, el render anterior al efecto ya dispara la petición que da 403.
  const scoped = knownProject;

  return (
    <ActivityBoard
      projectId={scoped ? projectId : null}
      tab={activeTab}
      onTabChange={setTab}
      extraTabs={[{ key: "usage", label: "Usage", Icon: Gauge }]}
      renderExtraTab={() => <HomePanel onCreateWorkflow={onCreateWorkflow} hideTitle />}
      scopeControl={
        <ScopeBar
          projects={projects}
          projectId={projectId}
          onProjectChange={setProject}
          loading={projectsLoading}
          showPeriod={false}
          allowAll={false}
          // En móvil esta barra sube a la fila del título del board.
          compact
        />
      }
      emptyScopeMessage={projectsLoading || projectList.length ? null : "No projects yet."}
    />
  );
}

// `hideTitle`: dentro de Activity la pestana ya dice "Usage" y la seccion ya
// dice "Activity". Repetirlo debajo era decir lo mismo tres veces.
function HomePanel({ onCreateWorkflow, hideTitle = false }) {
  const { currentAccount } = useOrganization();
  const accountId = currentAccount?.id || null;
  const { projects, loading: projectsLoading } = useProjects();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlProjectId = searchParams.get("project") || ALL_PROJECTS;
  // Mismo caso que en el board: al cambiar de organización el `?project=`
  // sobrevive en la URL y el proyecto no. Aquí no se corrige la URL —Usage sí
  // funciona con la cuenta entera— pero tampoco se manda un id que ya no es de
  // esta cuenta. Hasta que la lista responda no se descarta nada: una lista
  // vacía todavía no es prueba de que el proyecto no exista.
  const projectId =
    !urlProjectId || projectsLoading || (projects || []).some((p) => p.id === urlProjectId)
      ? urlProjectId
      : ALL_PROJECTS;
  const periodParam = searchParams.get("period");
  const period = PERIOD_DAYS[periodParam] ? periodParam : DEFAULT_PERIOD;

  const [metricsData, setMetricsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [assistantMeta, setAssistantMeta] = useState({});

  // Scope changes are written to the URL rather than to local state, and the rest of
  // the query string is preserved — the shell keeps the active view in `?view=`.
  const setScopeParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!accountId) return undefined;
    let cancelled = false;
    AssistantService.listAssistants({ accountId, limit: 100, view: "full" })
      .then((result) => {
        if (cancelled || !result?.success || !Array.isArray(result.data)) return;
        const map = {};
        result.data.forEach((assistant) => {
          const name = assistant?.name;
          if (!name) return;
          map[name] = {
            avatar: assistant?.avatar || null,
            model: assistant?.config?.modelLabel || null,
            isActive: assistant?.config?.isActive !== false,
            type: assistant?.type || null,
          };
        });
        setAssistantMeta(map);
      })
      .catch((error) => {
        console.warn("Could not load assistant metadata:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const fetchMetrics = useCallback(
    async ({ silent = false } = {}) => {
      if (!accountId) return;
      if (!silent) setLoading(true);

      try {
        const { since, until } = periodToRange(period);
        const data = await MetricsService.getDashboardMetrics({
          accountId,
          projectId: projectId || null,
          since,
          until,
        });
        setMetricsData(data || null);
        setErrorMessage("");
      } catch (error) {
        console.error("Error loading metrics:", error);
        // Deliberately does NOT fall back to an empty payload. Rendering zeros on a
        // failed fetch is precisely how this dashboard hid dead queries for months:
        // a broken request looked exactly like an account with no activity. The last
        // good data stays on screen, with the error stated above it.
        setErrorMessage(error?.message || "Metrics fetch failed");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [accountId, projectId, period]
  );

  // Load on mount and whenever the scope changes; refresh when the user comes back
  // to the tab. There is no interval: the old dashboard re-fetched every 45 seconds
  // whether or not anyone was looking, which kept the Lambda and the database busy
  // for every tab left open all day. A dashboard nobody is watching does not need to
  // be up to date — it needs to be up to date the moment they look at it again.
  useEffect(() => {
    if (!accountId) return undefined;

    fetchMetrics();

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        fetchMetrics({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [accountId, fetchMetrics]);

  const coins = metricsData?.coins || {};
  const workflows = metricsData?.workflows || {};
  const agents = metricsData?.agents || {};
  const bucket = metricsData?.period?.bucket || "day";
  const recentActivities = metricsData?.recent_activities || [];
  const projectScoped = Boolean(metricsData?.scope?.project_id);
  const periodDays = PERIOD_DAYS[period];

  const coinsSeries = useMemo(() => seriesValues(coins.over_time), [coins.over_time]);
  const workflowSeries = useMemo(
    () => seriesValues(workflows.over_time),
    [workflows.over_time]
  );
  const agentSeries = useMemo(() => seriesValues(agents.over_time), [agents.over_time]);

  const scopeBar = (
    <ScopeBar
      projects={projects}
      projectId={projectId}
      onProjectChange={(value) => setScopeParam("project", value)}
      period={period}
      onPeriodChange={(value) => setScopeParam("period", value)}
      loading={projectsLoading}
    />
  );

  if (loading && !metricsData) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={hideTitle ? null : "Usage"}
          subtitle={hideTitle ? null : "Coins spent, workflows run, agents called."}
          action={scopeBar}
        />
        <DashboardSkeleton />
      </div>
    );
  }

  // A failed first fetch shows the error, not a dashboard full of zeros.
  if (errorMessage && !metricsData) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={hideTitle ? null : "Usage"}
          subtitle={hideTitle ? null : "Coins spent, workflows run, agents called."}
          action={scopeBar}
        />
        <ErrorBanner message={errorMessage} onRetry={() => fetchMetrics()} />
      </div>
    );
  }

  const tabs = [
    { key: "overview", label: "Overview", Icon: LayoutDashboard },
    { key: "coins", label: "Coins", Icon: Coins },
    { key: "workflows", label: "Workflows", Icon: WorkflowIcon },
    { key: "agents", label: "Agents", Icon: Bot },
    { key: "activity", label: "Activity", Icon: Activity },
  ];

  return (
    <div className="space-y-6 opacity-0 animate-slide-blur [animation-fill-mode:forwards]">
      <PageHeader
        title={hideTitle ? null : "Usage"}
        subtitle={
          hideTitle
            ? null
            : projectScoped
              ? "Coins spent, workflows run and agents called — for this project."
              : "Coins spent, workflows run and agents called — across the account."
        }
        action={scopeBar}
      />

      <AnalyticsHero
        liveExecutions={formatCompact(toNumber(workflows.executions))}
        successRate={`${toNumber(workflows.success_rate).toFixed(1)}%`}
        execSeries={workflowSeries}
        successSeries={coinsSeries}
        healthy={!errorMessage}
        onExplore={() => setActiveTab("workflows")}
      />

      <DashboardTabs items={tabs} activeKey={activeTab} onChange={setActiveTab} />

      {/* A stale-but-visible dashboard with a failed refresh above it. Distinct from
          the first-fetch failure, which renders nothing but the error. */}
      {errorMessage && (
        <ErrorBanner message={errorMessage} onRetry={() => fetchMetrics()} stale />
      )}

      <div key={activeTab} className="animate-fade-in space-y-6 [animation-duration:250ms]">
        {activeTab === "overview" && (
          <OverviewTab
            coins={coins}
            workflows={workflows}
            agents={agents}
            coinsSeries={coinsSeries}
            workflowSeries={workflowSeries}
            agentSeries={agentSeries}
            periodDays={periodDays}
            onSeeFailures={() => setActiveTab("workflows")}
          />
        )}
        {activeTab === "coins" && (
          <CoinsTab coins={coins} bucket={bucket} projectScoped={projectScoped} />
        )}
        {activeTab === "workflows" && <WorkflowsTab workflows={workflows} bucket={bucket} />}
        {activeTab === "agents" && (
          <AgentsTab agents={agents} bucket={bucket} assistantMeta={assistantMeta} />
        )}
        {activeTab === "activity" && (
          <ActivityTab
            recentActivities={recentActivities}
            onCreateWorkflow={onCreateWorkflow}
          />
        )}
      </div>
    </div>
  );
}

function ErrorBanner({ message, onRetry, stale = false }) {
  return (
    <Card className="rounded-2xl border border-rose-400/40 bg-rose-500/10 backdrop-blur-md">
      <CardContent className="flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between md:p-5">
        <div className="space-y-1">
          <p className="text-sm font-medium text-rose-100">
            {stale ? "Couldn't refresh — showing the last good data" : "Metrics fetch failed"}
          </p>
          <p className="break-all font-mono text-xs text-rose-200/80">{message}</p>
        </div>
        <Button
          variant="outline"
          className="w-fit border-rose-400/40 bg-rose-600/10 text-rose-100 hover:bg-rose-600/20"
          onClick={onRetry}
        >
          Retry now
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Overview — one KPI per family, then the numbers worth acting on
// ---------------------------------------------------------------------------

function OverviewTab({
  coins,
  workflows,
  agents,
  coinsSeries,
  workflowSeries,
  agentSeries,
  periodDays,
  onSeeFailures,
}) {
  const hint = `vs previous ${periodDays} days`;
  const successRate = toNumber(workflows.success_rate);
  const failed = toNumber(workflows.failed);
  const coverage = toNumber(agents.cost_data_coverage_pct);

  const kpis = [
    {
      label: "Coins spent",
      value: formatCoins(coins.total_spent),
      delta: coins.delta,
      hint,
      Icon: Coins,
      tone: "amber",
      series: coinsSeries,
    },
    {
      label: "Workflow runs",
      value: formatCompact(toNumber(workflows.executions)),
      delta: workflows.delta,
      hint,
      Icon: WorkflowIcon,
      tone: "violet",
      series: workflowSeries,
    },
    {
      label: "Agent calls",
      value: formatCompact(toNumber(agents.total_calls)),
      delta: agents.delta,
      hint,
      Icon: Bot,
      tone: "pink",
      series: agentSeries,
    },
    {
      label: "Est. cost",
      value: formatUsd(agents.estimated_cost_usd),
      // No delta: the number is built from partial data (see CoverageNotice), and a
      // period-over-period change on a partial number would compound the error.
      hint: coverage >= 99.5 ? "LLM spend" : `from ${coverage.toFixed(0)}% of calls — a floor`,
      Icon: Sparkles,
      tone: "violet",
      series: agentSeries,
    },
  ];

  return (
    <>
      {/* Dos columnas en móvil y no una: una tarjeta por fila son ocho pantallas
          de scroll para ocho números que se comparan entre sí.
          Cuatro tampoco: medido a 360/390/430px, el hueco útil de la tarjeta se
          queda en 55-65px y un valor de cinco caracteres —"15.8K"— necesita 81
          al tamaño al que está escrito. Para meter cuatro habría que bajar el
          número a 20px, que es justo lo contrario de para lo que está la
          tarjeta. */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {kpis.map((item) => (
          <StatCard key={item.label} {...item} />
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <HighlightCard label="Success rate" hint="Completed vs. failed runs">
          {/* Apilado en móvil: el número a 30px más el anillo de 64 piden 157px
              y la tarjeta a dos columnas da 134. */}
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <span className="text-2xl font-semibold tabular-nums text-white sm:text-3xl">
              <AnimatedValue value={`${successRate.toFixed(1)}%`} />
            </span>
            <GaugeRing value={successRate} size={56} stroke={6}>
              <span className="text-[11px] font-semibold tabular-nums text-pink-200">
                {Math.round(successRate)}%
              </span>
            </GaugeRing>
          </div>
        </HighlightCard>

        <HighlightCard
          label="Failed runs"
          hint={failed > 0 ? "Tap Workflows to see why" : "Nothing failed"}
          accent={
            failed > 0 ? <AlertTriangle className="h-4 w-4 text-rose-300" /> : null
          }
        >
          <button
            type="button"
            onClick={failed > 0 ? onSeeFailures : undefined}
            className={cn(
              "flex w-full flex-col items-start gap-2 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-3",
              failed > 0 ? "cursor-pointer" : "cursor-default"
            )}
          >
            <span
              className={cn(
                "text-2xl font-semibold tabular-nums sm:text-3xl",
                failed > 0 ? "text-rose-200" : "text-white"
              )}
            >
              <AnimatedValue value={formatCompact(failed)} />
            </span>
            <CheckCircle
              className={cn(
                "h-8 w-8 sm:h-9 sm:w-9",
                failed > 0 ? "text-rose-400/30" : "text-emerald-400/40"
              )}
              strokeWidth={1.5}
            />
          </button>
        </HighlightCard>

        {/* Median, never mean. On dev the mean completed-run duration is ~32 minutes
            and the median is 16 seconds — the mean is destroyed by a long tail of
            paused runs that close a day later, and would tell the user their
            workflows take half an hour. */}
        <HighlightCard label="Median run" hint={`p95 ${formatDuration(workflows.p95_duration_seconds)}`}>
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <span className="text-2xl font-semibold tabular-nums text-white sm:text-3xl">
              <AnimatedValue value={formatDuration(workflows.median_duration_seconds)} />
            </span>
            <Clock className="h-8 w-8 text-violet-300/40 sm:h-9 sm:w-9" strokeWidth={1.5} />
          </div>
        </HighlightCard>

        <HighlightCard label="Running now" hint="Not yet completed or failed">
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <span className="text-2xl font-semibold tabular-nums text-white sm:text-3xl">
              <AnimatedValue value={formatCompact(toNumber(workflows.running))} />
            </span>
            <Zap className="h-8 w-8 text-violet-300/40 sm:h-9 sm:w-9" strokeWidth={1.5} />
          </div>
        </HighlightCard>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Coins — the resource the account actually pays in, and never showed before
// ---------------------------------------------------------------------------

function CoinsTab({ coins, bucket, projectScoped }) {
  return (
    <>
      <SeriesChart
        data={coins.over_time}
        bucket={bucket}
        title="Coin spend over time"
        description="Coins deducted, bucketed by the period you selected."
        valueLabel="Coins"
        tone="amber"
        formatValue={formatCoins}
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CoinsByResourceCard
          byResourceType={coins.by_resource_type}
          unattributedCoins={coins.unattributed_coins}
          unattributedPct={coins.unattributed_pct}
          projectScoped={projectScoped}
        />
        <TopSpendersCard topSpenders={coins.top_spenders} />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Workflows — what ran, and whether it worked
// ---------------------------------------------------------------------------

function WorkflowsTab({ workflows, bucket }) {
  const stats = [
    {
      label: "Completed",
      value: formatCompact(toNumber(workflows.completed)),
      hint: "Finished successfully",
      Icon: CheckCircle,
      tone: "violet",
    },
    {
      label: "Failed",
      value: formatCompact(toNumber(workflows.failed)),
      hint: "Ended in error",
      Icon: AlertTriangle,
      tone: "pink",
    },
    {
      label: "Cancelled",
      value: formatCompact(toNumber(workflows.cancelled)),
      hint: "Stopped by a user",
      Icon: ArrowDownLeft,
      tone: "violet",
    },
    {
      label: "Success rate",
      value: `${toNumber(workflows.success_rate).toFixed(1)}%`,
      hint: "Of terminal runs",
      Icon: Gauge,
      tone: "pink",
    },
  ];

  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((item) => (
          <StatCard key={item.label} {...item} />
        ))}
      </div>

      <SeriesChart
        data={workflows.over_time}
        bucket={bucket}
        title="Executions over time"
        description="Workflow runs started in each bucket."
        valueLabel="Runs"
        tone="violet"
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="xl:col-span-3">
          <TopWorkflowsCard topWorkflows={workflows.top_workflows} />
        </div>
        <div className="xl:col-span-2">
          <RecentFailuresCard recentFailures={workflows.recent_failures} />
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Agents — real LLM calls, and what they burned
// ---------------------------------------------------------------------------

function AgentsTab({ agents, bucket, assistantMeta }) {
  const stats = [
    {
      label: "Calls",
      value: formatCompact(toNumber(agents.total_calls)),
      hint: "LLM calls made",
      Icon: Bot,
      tone: "violet",
    },
    {
      label: "Input tokens",
      value: formatCompact(toNumber(agents.input_tokens)),
      hint: "Where usage was reported",
      Icon: ArrowUpRight,
      tone: "violet",
    },
    {
      label: "Output tokens",
      value: formatCompact(toNumber(agents.output_tokens)),
      hint: "Where usage was reported",
      Icon: ArrowDownLeft,
      tone: "pink",
    },
    {
      label: "Est. cost",
      value: formatUsd(agents.estimated_cost_usd),
      hint: "A floor — see below",
      Icon: Sparkles,
      tone: "violet",
    },
  ];

  return (
    <>
      <CoverageNotice
        totalCalls={agents.total_calls}
        callsWithCostData={agents.calls_with_cost_data}
        coveragePct={agents.cost_data_coverage_pct}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((item) => (
          <StatCard key={item.label} {...item} />
        ))}
      </div>

      <SeriesChart
        data={agents.over_time}
        bucket={bucket}
        title="Agent calls over time"
        description="LLM calls made by your agents."
        valueLabel="Calls"
        tone="pink"
      />

      <AgentScoreboard data={agents.top_agents} meta={assistantMeta} />
      <ModelUsageCard byModel={agents.by_model} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Activity
// ---------------------------------------------------------------------------

function ActivityTab({ recentActivities, onCreateWorkflow }) {
  if (!recentActivities.length) {
    return (
      <Card className="rounded-2xl border border-white/10 bg-[#0a0a0a]/85">
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <Activity className="h-9 w-9 text-gray-600" strokeWidth={1.5} />
          <p className="text-sm text-gray-300">Nothing happened in this period.</p>
          <Button variant="outline" onClick={onCreateWorkflow}>
            Create a workflow
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Suspense fallback={<SectionSkeleton columns={1} />}>
      <RecentActivities data={recentActivities} />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Shared cards
// ---------------------------------------------------------------------------

/**
 * A Delta, rendered honestly.
 *
 * `change_pct` is null — not 0 — when the previous period had nothing to compare
 * against. "0%" would read as "flat" when the truth is "brand new", so a null
 * becomes a "New" chip instead.
 */
function DeltaBadge({ delta }) {
  if (!delta) return null;

  const change = formatChange(delta.change_pct);
  if (change == null) {
    if (toNumber(delta.current) <= 0) return null;
    return (
      <span className="inline-flex items-center rounded-md bg-violet-500/15 px-1.5 py-0.5 text-xs font-medium text-violet-300">
        New
      </span>
    );
  }

  const positive = toNumber(delta.change_pct) >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownLeft;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums",
        positive ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"
      )}
    >
      <Icon className="h-3 w-3" />
      {change}
    </span>
  );
}

function HighlightCard({ label, hint, accent, children }) {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-[#0a0f1f]/70 p-3 shadow-[0_10px_30px_rgba(10,6,32,0.35)] sm:p-5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
          {label}
        </span>
        {accent}
      </div>
      {children}
      {hint && <span className="text-xs text-gray-400">{hint}</span>}
    </div>
  );
}

const STAT_TONES = {
  violet: "bg-violet-500/15 text-violet-200 ring-violet-400/20",
  pink: "bg-pink-500/15 text-pink-300 ring-pink-400/20",
  amber: "bg-amber-500/15 text-amber-200 ring-amber-400/20",
};

function StatCard({ label, value, delta, hint, Icon, tone = "violet", series }) {
  // Sparkline only speaks violet and pink; amber cards borrow the violet line rather
  // than rendering an off-palette one.
  const sparkTone = tone === "pink" ? "pink" : "violet";

  return (
    <div className="relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-transparent bg-[#0a0f1f]/70 p-3 shadow-[0_10px_30px_rgba(10,6,32,0.35)] sm:p-6">
      {/* Icono encima de la etiqueta en móvil: al lado, los 36px del círculo
          más el texto se pasan del ancho de la tarjeta a dos columnas. */}
      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
        <span
          className={cn(
            "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ring-1 sm:h-12 sm:w-12",
            STAT_TONES[tone] || STAT_TONES.violet
          )}
        >
          {Icon && <Icon className="h-5 w-5 sm:h-6 sm:w-6" />}
        </span>
        <span className="text-[10px] font-semibold uppercase leading-tight tracking-[0.14em] text-gray-400 sm:text-[11px]">
          {label}
        </span>
      </div>
      <span className="mt-2.5 text-2xl font-semibold tracking-tight tabular-nums text-white sm:mt-4 sm:text-5xl">
        <AnimatedValue value={value} />
      </span>
      {/* Lo mismo con el delta y la pista: "+15800.0%" y "vs previous 30 days"
          juntos piden 167px y la tarjeta da 111. */}
      <div className="mt-1 flex flex-col items-start gap-1 text-[11px] text-gray-400 sm:mt-2 sm:flex-row sm:items-center sm:gap-2 sm:text-xs">
        <DeltaBadge delta={delta} />
        {hint && <span>{hint}</span>}
      </div>
      {Array.isArray(series) && series.filter((v) => Number.isFinite(Number(v))).length >= 2 && (
        <Sparkline values={series} tone={sparkTone} height={46} className="mt-3 -mx-1.5 sm:mt-5" />
      )}
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-44 rounded-3xl border border-white/10 bg-white/[0.03] skeleton" />
      <SectionSkeleton columns={4} />
      <SectionSkeleton columns={2} />
    </div>
  );
}

function SectionSkeleton({ columns }) {
  return (
    <div
      className={`grid grid-cols-1 gap-6 ${columns > 1 ? "md:grid-cols-2" : ""} ${
        columns > 2 ? "xl:grid-cols-3" : ""
      } ${columns > 3 ? "2xl:grid-cols-4" : ""}`}
    >
      {Array.from({ length: columns }).map((_, index) => (
        <div
          key={index}
          className="h-64 rounded-2xl border border-white/10 bg-white/[0.03] skeleton"
        />
      ))}
    </div>
  );
}

/** SeriesPoint[] → the plain number[] a Sparkline wants. */
function seriesValues(points) {
  return (Array.isArray(points) ? points : []).map((point) => toNumber(point?.value));
}
